import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, LogOut, Monitor, KeyRound, RefreshCw, Mail, ShieldOff, Copy, Check } from "lucide-react";
import ReAuthModal from "../components/ReAuthModal";

export default function Security() {
  const { user, refreshUser } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {scope, title, then}
  const [recovery, setRecovery] = useState([]);
  const [copied, setCopied] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const load = () => { setLoading(true); api.get("/auth/sessions").then((r) => setSessions(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openReauth = (scope, title, then) => setModal({ scope, title, then });

  const revokeOne = async (sid) => {
    try { await api.post(`/auth/sessions/${sid}/revoke`); toast.success("Oturum kapatıldı"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const revokeOthers = async () => {
    try { const { data } = await api.post("/auth/sessions/revoke-others"); toast.success(`${data.revoked} diğer oturum kapatıldı`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const disable2FA = (reauthToken) => api.post("/auth/totp/disable", {}, { headers: { "X-Reauth-Token": reauthToken } })
    .then(() => { toast.success("2FA kapatıldı — yeniden kurmanız gerekecek"); refreshUser(); })
    .catch((e) => toast.error(e?.response?.data?.detail || "İşlem başarısız"));

  const regenRecovery = (reauthToken) => api.post("/auth/recovery-codes/regenerate", {}, { headers: { "X-Reauth-Token": reauthToken } })
    .then((r) => { setRecovery(r.data.recovery_codes); toast.success("Kurtarma kodları yenilendi — eski kodlar geçersiz"); })
    .catch((e) => toast.error(e?.response?.data?.detail || "İşlem başarısız"));

  const changeEmail = (reauthToken) => {
    if (!newEmail.includes("@")) return toast.error("Geçerli bir e-posta girin");
    return api.post("/auth/change-email", { new_email: newEmail.trim().toLowerCase() }, { headers: { "X-Reauth-Token": reauthToken } })
      .then((r) => { toast.success("E-posta güncellendi. Tüm diğer oturumlar kapatıldı."); setNewEmail(""); refreshUser(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "İşlem başarısız"));
  };

  const copyRecovery = () => {
    navigator.clipboard.writeText(recovery.join("\n")).then(() => { setCopied(true); toast.success("Panoya kopyalandı"); }).catch(() => toast.error("Kopyalanamadı"));
  };

  return (
    <div data-testid="security-root" className="max-w-5xl">
      <div className="mb-6">
        <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">HESAP GÜVENLİĞİ</div>
        <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900">Güvenlik</h1>
      </div>

      {/* Active sessions */}
      <section className="card-lux p-5 mb-6" data-testid="sessions-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Monitor className="w-5 h-5 text-amber-700" /><h2 className="font-serif-display text-xl">Aktif Oturumlar</h2></div>
          <button data-testid="revoke-others" onClick={revokeOthers} className="text-xs px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 inline-flex items-center gap-1"><LogOut className="w-3.5 h-3.5" /> Diğer Tüm Cihazlardan Çıkış</button>
        </div>
        {loading ? <div className="text-stone-500 text-sm">Yükleniyor...</div> : (
          <div className="divide-y divide-stone-100">
            {sessions.map((s) => (
              <div key={s.id} className="py-3 flex items-center justify-between" data-testid={`session-row-${s.id}`}>
                <div>
                  <div className="text-sm font-medium">{s.device} {s.is_current && <span className="ml-2 text-[10px] font-mono-code text-emerald-700">MEVCUT</span>}</div>
                  <div className="text-xs text-stone-500 font-mono-code">IP: {s.ip || "?"} · Son: {new Date(s.last_used_at).toLocaleString("tr-TR")}</div>
                  <div className="text-[10px] text-stone-400 mt-0.5 truncate max-w-md">{s.user_agent}</div>
                </div>
                {!s.is_current && <button data-testid={`revoke-${s.id}`} onClick={() => revokeOne(s.id)} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50">Kapat</button>}
              </div>
            ))}
            {sessions.length === 0 && <div className="text-sm text-stone-500 py-3">Aktif oturum yok</div>}
          </div>
        )}
      </section>

      {/* Owner-only critical actions */}
      {user?.role === "owner" && (
        <>
          <section className="card-lux p-5 mb-6">
            <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5 text-amber-700" /><h2 className="font-serif-display text-xl">İki Faktörlü Doğrulama (TOTP)</h2></div>
            <p className="text-xs text-stone-500 mb-3">2FA aktif. Kritik değişiklikler için yeniden doğrulama gerekir.</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="btn-regen-recovery" onClick={() => openReauth("recovery_regen", "Kurtarma kodlarını yenile — eski 10 kod anında geçersiz olacak.", regenRecovery)} className="text-xs px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Kurtarma Kodlarını Yenile</button>
              <button data-testid="btn-disable-2fa" onClick={() => openReauth("totp_disable", "2FA'yı kapatmak Owner hesabınızın güvenliğini düşürür — kapattıktan sonra yeniden kurmanız zorunlu tutulacak.", disable2FA)} className="text-xs px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"><ShieldOff className="w-3.5 h-3.5" /> 2FA'yı Kapat</button>
            </div>
            {recovery.length > 0 && (
              <div className="mt-4" data-testid="regen-recovery-block">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 mb-2">Yeni kurtarma kodları oluşturuldu. Güvenli bir yerde saklayın. Her kod yalnızca bir kez kullanılabilir.</div>
                <div className="grid grid-cols-2 gap-2 mb-2 font-mono-code text-sm">
                  {recovery.map((c, i) => <div key={i} className="p-2 border border-stone-200 rounded bg-stone-50 text-center">{c}</div>)}
                </div>
                <button onClick={copyRecovery} className="w-full py-2 rounded-lg border border-stone-200 text-sm inline-flex items-center justify-center gap-2 hover:bg-stone-50" data-testid="regen-copy-recovery">
                  {copied ? <><Check className="w-4 h-4" /> Kopyalandı</> : <><Copy className="w-4 h-4" /> Panoya Kopyala</>}
                </button>
              </div>
            )}
          </section>

          <section className="card-lux p-5 mb-6">
            <div className="flex items-center gap-2 mb-3"><Mail className="w-5 h-5 text-amber-700" /><h2 className="font-serif-display text-xl">Owner E-postası</h2></div>
            <p className="text-xs text-stone-500 mb-3">Mevcut e-posta: <b>{user.email}</b>. Değiştirmek için yeniden doğrulama gerekir. Değişiklik sonrası tüm diğer oturumlar kapatılır.</p>
            <div className="flex gap-2">
              <input data-testid="new-email-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="yeni-eposta@rivaco.com" type="email" className="flex-1 px-3 py-2 rounded-lg border border-stone-200 outline-none focus:border-amber-500" />
              <button data-testid="btn-change-email" onClick={() => openReauth("change_email", "Owner e-posta adresini değiştirmek yüksek güvenlikli bir işlem; şifre + Authenticator kodu gerekir.", changeEmail)} className="text-xs px-3 py-2 rounded-lg gold-gradient text-white">Değiştir</button>
            </div>
          </section>
        </>
      )}

      <ReAuthModal
        open={!!modal}
        scope={modal?.scope}
        title={modal?.title}
        ownerHasTotp={!!user?.totp_enabled}
        onClose={() => setModal(null)}
        onSuccess={(tok) => { const then = modal?.then; setModal(null); then && then(tok); }}
      />
    </div>
  );
}
